// The main navigation: top-level views and dropdowns (App.jsx renders them).
// Plain data, kept here so the header brand can be tested against every destination.
// `requiresIbcsNav` / `requiresJiatfNav` gate a dropdown behind its Platform Settings switch.

export const VIEWS = [
  {
    id: 'istar',
    path: '/istar',
    name: 'ISTAR',
    description: 'Intelligence, Surveillance, Target Acquisition & Reconnaissance — direct entry into the intelligence collection workflow',
    icon: '📡',
    category: 'primary',
    tooltip: {
      acronym: 'Intelligence · Surveillance\nTarget Acquisition · Reconnaissance',
      body: 'Direct entry into the intelligence\ncollection workflow — feeds the\nweaponeering target funnel',
    },
  },

  // Core Mission Workflow
  {
    id: 'ai-planner',
    path: '/',
    name: 'Mission Planning',
    description: 'AI-enhanced objectives-based mission planning with intelligent resource allocation',
    icon: '/tomahawk-icon.svg',
    category: 'primary'
  },

  {
    id: 'theater',
    path: '/strike-planning',
    name: 'Strike Planning',
    description: 'Resource planning, platform allocation, OSINT intelligence, AI planning, and theater simulation',
    icon: '⚔️',
    category: 'primary'
  },

  // Strike Execution Dropdown (IBCS-style): the execution console itself,
  // plus the Strike Practice section — stress the plan against doctrine-based
  // adversary laydowns (Adversary Deployment Planning, EP-39).
  {
    id: 'execution',
    name: 'Strike Execution',
    description: 'Review and distribute strike order packages, and stress plans against adversary doctrine (Strike Practice)',
    icon: '🚀',
    category: 'dropdown',
    subItems: [
      {
        id: 'strike-execution',
        path: '/execution',
        name: 'Strike Execution',
        description: 'Review and distribute strike order packages to operational units',
        icon: '🚀'
      },
      {
        id: 'strike-practice-header',
        sectionHeader: true,
        name: 'Strike Practice'
      },
      {
        id: 'adversary-laydowns',
        path: '/adversary-deployment',
        name: 'Adversary Deployment Plan',
        description: 'Generate doctrine-based red air-defense laydowns (high/medium/low coverage) around a mission’s targets and test the strike plan against them',
        icon: '🔺'
      }
    ]
  },

  {
    id: 'aar',
    path: '/aar/dashboard',
    name: 'After Action Review',
    description: 'Battle damage assessment and post-execution feedback for AI learning',
    icon: '📊',
    category: 'primary',
    badge: 'BETA'
  },

  // System & Weaponeering Dropdown
  {
    id: 'system-weaponeering',
    name: 'System & Weaponeering',
    description: 'Platform management and target weaponeering',
    icon: '⚙️',
    category: 'dropdown',
    subItems: [
      {
        id: 'platform-mgmt',
        path: '/platform-management',
        name: 'Platform Management',
        description: 'Launch platform status, resource management, and system health monitoring',
        icon: '🛡️'
      },
      {
        id: 'weaponeering',
        path: '/weaponeering',
        name: 'Target Weaponeering',
        description: 'AI-assisted target development from intelligence to mission-ready targets with aim point and ordnance selection',
        icon: '⌖'
      },
      {
        id: 'review-queue',
        path: '/weaponeering/review-queue',
        name: 'Review Queue',
        description: 'Deliberate targeting refresh cycle — overdue and due-soon aim points requiring planner review',
        icon: '⏱'
      },
      {
        id: 'analytics',
        path: '/analytics',
        name: 'Analytics Dashboard',
        description: 'Kill Chain metrics: Intelligence → Weaponeering → Mission Planning → Execution performance',
        icon: '📈'
      }
    ]
  },

  // Integrated Air & Missile Defense Dropdown
  // Visibility is gated by the IBCS nav setting (Platform Management → Interface).
  {
    id: 'ibcs',
    name: 'IBCS',
    description: 'Integrated Air & Missile Defense — deployment planning and dynamic engagement',
    icon: '🛡️',
    category: 'dropdown',
    requiresIbcsNav: true,
    subItems: [
      {
        id: 'deployment-plans',
        path: '/deployment-plans',
        name: 'Deployment Plans',
        description: 'Plan a defended-area laydown, or launch an accepted course of action into dynamic engagement',
        icon: '🛰️'
      },
      {
        id: 'dynamic-engagement',
        path: '/dynamic-engagement',
        name: 'Dynamic Engagement',
        description: 'Live engagement theater — sensors, effectors, and Monte Carlo evaluation of the integrated defense',
        icon: '💥'
      }
    ]
  },

  // Counter-small-UAS (JIATF-401) Dropdown — same model as IBCS, its own C-UAS sites and plans.
  // Visibility is gated by the JIATF-401 nav setting (Platform Management → Interface).
  {
    id: 'jiatf401',
    name: 'JIATF-401',
    description: 'Counter-small-UAS — site defense planning and dynamic engagement against drones',
    icon: '🛸',
    category: 'dropdown',
    requiresJiatfNav: true,
    subItems: [
      {
        id: 'jiatf-deployment-plans',
        path: '/jiatf-401/deployment-plans',
        name: 'Deployment Plans',
        description: 'Pick a pre-staged area or start a site anywhere, plan the counter-UAS laydown, or launch an accepted plan',
        icon: '🛰️'
      },
      {
        id: 'jiatf-dynamic-engagement',
        path: '/jiatf-401/dynamic-engagement',
        name: 'Dynamic Engagement',
        description: 'Drone raids against an accepted counter-UAS plan — sensors, effectors and the kill chain',
        icon: '💥'
      }
    ]
  },

  // Experimental R&D Features (hidden by default, accessible via Dev Tools)
  {
    id: '3d-theater',
    path: '/3d-theater',
    name: '3D Theater (R&D)',
    description: 'Experimental 3D/4D battle theater visualization with terrain, missiles, and threat volumes',
    icon: '🌐',
    category: 'experimental',
    hidden: true
  },

  // Development Tools (hidden by default, accessible via Dev Tools)
  {
    id: 'scenario',
    path: '/scenario',
    name: 'Scenario Runner (Dev)',
    description: 'Development testing interface for scenario simulation',
    icon: '🎬',
    category: 'development',
    hidden: true
  },
  {
    id: 'vectors',
    path: '/approach-vectors',
    name: 'Approach Vectors (Dev)',
    description: 'Standalone approach vector visualization',
    icon: '🚀',
    category: 'development',
    hidden: true
  },
  {
    id: 'profile',
    path: '/flight-profile',
    name: 'Flight Profile (Dev)',
    description: 'Standalone flight profile analysis',
    icon: '📊',
    category: 'development',
    hidden: true
  },
  {
    id: 'planner',
    path: '/mission-planner',
    name: 'Mission Planner (Dev)',
    description: 'Original mission planning interface',
    icon: '🗺️',
    category: 'development',
    hidden: true
  },
];
